import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { submitContactMessage } from "@/lib/contact.functions";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export function ContactModal({ open, onOpenChange, context = "general" }: { open: boolean; onOpenChange: (o: boolean) => void; context?: "general" | "press" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const submit = useServerFn(submitContactMessage);
  const m = useMutation({
    mutationFn: () => submit({ data: { name, email, message } }),
    onSuccess: () => {
      toast.success("Message sent — we'll get back to you soon.");
      setName(""); setEmail(""); setMessage("");
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't send. Try again."),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> {context === "press" ? "Press inquiry" : "Get in touch"}</DialogTitle>
          <DialogDescription>
            {context === "press"
              ? "For interviews, background, or media requests — we read every inquiry."
              : "Questions, feedback, partnership ideas — we read every message."}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); if (!m.isPending) m.mutate(); }}
          className="space-y-3 pt-1"
        >
          <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
          <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} required />
          <Textarea placeholder={context === "press" ? "Publication, deadline, and what you need…" : "What's on your mind?"} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={4000} rows={5} required />
          <Button type="submit" disabled={m.isPending || !name.trim() || !email.trim() || !message.trim()} className="w-full">
            {m.isPending ? "Sending…" : "Send message"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
